import React, { useState } from 'react';
import { cn } from '../../lib/utils';

type CalendarEvent = {
  id: string | number;
  title: string;
  date: Date;
  type: 'deadline' | 'upload' | 'validation' | 'approval';
  status?: 'pending' | 'completed' | 'overdue' | 'at-risk';
};

type CalendarProps = {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  className?: string;
};

export const Calendar = ({ events, onEventClick, className }: CalendarProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<'month' | 'week'>('month');

  // Get current month and year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Get days in month
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  
  // Get first day of month
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  // Create array of day numbers for the month
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Create array for empty cells before first day
  const emptyCellsBefore = Array.from({ length: firstDayOfMonth }, (_, i) => null);

  // Get all days including empty cells
  const allDays = [...emptyCellsBefore, ...days];

  // Group events by date
  const eventsByDate: Record<string, CalendarEvent[]> = {};
  
  events.forEach(event => {
    const dateKey = event.date.toISOString().split('T')[0];
    if (!eventsByDate[dateKey]) {
      eventsByDate[dateKey] = [];
    }
    eventsByDate[dateKey].push(event);
  });

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  // Navigate to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format month name
  const monthName = currentDate.toLocaleString('default', { month: 'long' });

  // Get event status color
  const getEventStatusColor = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500 border-green-600';
      case 'overdue':
        return 'bg-destructive border-destructive/70';
      case 'at-risk':
        return 'bg-amber-500 border-amber-600';
      default:
        return 'bg-blue-500 border-blue-600';
    }
  };

  // Get event type icon
  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'deadline':
        return '⏰';
      case 'upload':
        return '📤';
      case 'validation':
        return '✓';
      case 'approval':
        return '👍';
      default:
        return '📅';
    }
  };

  return (
    <div className={cn("bg-card rounded-lg border border-border overflow-hidden", className)}>
      {/* Calendar Header */}
      <div className="p-4 flex justify-between items-center border-b border-border">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold">{monthName} {currentYear}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setCurrentView('month')}
            className={cn(
              "px-3 py-1 rounded-md text-sm font-medium",
              currentView === 'month' 
                ? "bg-primary text-primary-foreground" 
                : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
            )}
          >
            Month
          </button>
          <button 
            onClick={() => setCurrentView('week')}
            className={cn(
              "px-3 py-1 rounded-md text-sm font-medium",
              currentView === 'week' 
                ? "bg-primary text-primary-foreground" 
                : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
            )}
          >
            Week
          </button>
          <button 
            onClick={goToPreviousMonth}
            className="p-1 rounded-md hover:bg-muted"
          >
            ◀
          </button>
          <button 
            onClick={goToToday}
            className="px-3 py-1 rounded-md text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80"
          >
            Today
          </button>
          <button 
            onClick={goToNextMonth}
            className="p-1 rounded-md hover:bg-muted"
          >
            ▶
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
            <div 
              key={day} 
              className={cn(
                "text-center py-2 font-medium text-sm",
                index === 0 || index === 6 ? "text-muted-foreground" : ""
              )}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1">
          {allDays.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="h-24 bg-muted/30 rounded-md"></div>;
            }

            const date = new Date(currentYear, currentMonth, day);
            const dateKey = date.toISOString().split('T')[0];
            const dayEvents = eventsByDate[dateKey] || [];
            const isToday = new Date().toDateString() === date.toDateString();

            return (
              <div 
                key={`day-${day}`} 
                className={cn(
                  "h-24 p-1 border border-border/50 rounded-md overflow-hidden",
                  isToday ? "bg-primary/5 border-primary/30" : "hover:bg-muted/20"
                )}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className={cn(
                    "text-sm font-medium",
                    isToday ? "text-primary bg-primary/10 px-1.5 py-0.5 rounded-full" : ""
                  )}>
                    {day}
                  </span>
                  {dayEvents.length > 0 && (
                    <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                      {dayEvents.length}
                    </span>
                  )}
                </div>
                <div className="space-y-1 overflow-y-auto max-h-[calc(100%-20px)]">
                  {dayEvents.slice(0, 3).map((event) => (
                    <div
                      key={event.id}
                      onClick={() => onEventClick?.(event)}
                      className={cn(
                        "text-xs px-1.5 py-1 rounded border-l-2 truncate cursor-pointer",
                        getEventStatusColor(event.status),
                        "bg-opacity-15 hover:bg-opacity-25 transition-colors"
                      )}
                    >
                      <span className="mr-1">{getEventTypeIcon(event.type)}</span>
                      {event.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-muted-foreground text-center">
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}; 